import { UserRole } from '@/model/User.model';
import { RouteData } from '@/router/routes';
import { lazyWithReload } from '@/router/utils';
import Error from '@/routes/Error';
import SchoolLayout from '@/routes/SchoolApp/layout';

// Common
const SchoolHome = lazyWithReload(() => import('@/routes/SchoolApp/Common/SchoolHome/SchoolHome'));
const SchoolClassroom = lazyWithReload(() => import('@/routes/SchoolApp/Common/SchoolClassroom/SchoolClassroom'));

// SchoolGroup
const SchoolGroupClassroomList = lazyWithReload(
  () => import('@/routes/SchoolApp/Common/SchoolGroupClassroomList/SchoolGroupClassroomList')
);
const SchoolGroupSchoolList = lazyWithReload(
  () => import('@/routes/SchoolApp/Common/SchoolGroupSchoolList/SchoolGroupSchoolList')
);
const SchoolGroupSchoolDetail = lazyWithReload(
  () => import('@/routes/SchoolApp/Common/SchoolGroupSchoolDetail/SchoolGroupSchoolDetail')
);
const SchoolGroupUserList = lazyWithReload(
  () => import('@/routes/SchoolApp/Common/SchoolGroupUserList/SchoolGroupUserList')
);
const SchoolGroupDashboard = lazyWithReload(
  () => import('@/routes/SchoolApp/Common/SchoolGroupDashboard/SchoolGroupDashboard')
);

// Switchable
const SchoolDashboardSwitcher = lazyWithReload(() => import('@/routes/SchoolApp/Common/SchoolDashboardSwitcher'));
const SchoolClassroomTaxonomyInsights = lazyWithReload(
  () => import('@/routes/SchoolApp/Common/SchoolClassroomTaxonomy/SchoolClassroomTaxonomyInsights')
);
const SchoolLiveSessionList = lazyWithReload(
  () => import('@/routes/SchoolApp/Common/SchoolLiveSession/SchoolLiveSessionList')
);
const SchoolLiveSessionHost = lazyWithReload(
  () => import('@/routes/SchoolApp/Common/SchoolLiveSession/host/TeacherLiveSessionHost')
);

enum SchoolRouteKeys {
  // Common
  SchoolHome = 'SchoolHome',
  SchoolClassroomList = 'SchoolClassroomList',

  // SchoolGroup
  SchoolGroupClassroomList = 'SchoolGroupClassroomList',
  SchoolGroupSchoolList = 'SchoolGroupSchoolList',
  SchoolGroupSchoolDetail = 'SchoolGroupSchoolDetail',
  SchoolGroupUserList = 'SchoolGroupUserList',
  SchoolGroupDashboard = 'SchoolGroupDashboard',

  // Switchable
  SchoolClassroomDetail = 'SchoolClassroomDetail',
  SchoolClassroomOverview = 'SchoolClassroomOverview',
  SchoolClassroomInsights = 'SchoolClassroomInsights',
  SchoolStudentList = 'SchoolStudentList',
  SchoolStudentDetail = 'SchoolStudentDetail',
  SchoolLessonList = 'SchoolLessonList',
  SchoolLessonDetail = 'SchoolLessonDetail',
  SchoolLiveSessionList = 'SchoolLiveSessionList',
  SchoolLiveSessionHost = 'SchoolLiveSessionHost',
}

const schoolRoutes = {
  // Common
  [SchoolRouteKeys.SchoolHome]: {
    path: '/school-app',
    component: SchoolHome,
    errorComponent: Error,
    layoutComponent: SchoolLayout,
    getRoute: () => {
      return `/school-app`;
    },
  },
  /** @deprecated Legacy classroom list page, use {@link schoolRoutes.SchoolGroupClassroomList | SchoolGroupClassroomList} instead. */
  [SchoolRouteKeys.SchoolClassroomList]: {
    path: '/school-app/schools/:schoolId/classrooms',
    component: SchoolClassroom,
    errorComponent: Error,
    layoutComponent: SchoolLayout,
    getRoute: ({ schoolId }: { schoolId: string }) => {
      return `/school-app/schools/${schoolId}/classrooms`;
    },
  },
  [SchoolRouteKeys.SchoolGroupSchoolDetail]: {
    path: '/school-app/schools/:schoolId',
    component: SchoolGroupSchoolDetail,
    errorComponent: Error,
    layoutComponent: SchoolLayout,
    getRoute: ({ schoolId }: { schoolId: string }) => {
      return `/school-app/schools/${schoolId}`;
    },
  },

  // SchoolGroup
  [SchoolRouteKeys.SchoolGroupDashboard]: {
    path: '/school-app/dashboard',
    component: SchoolGroupDashboard,
    errorComponent: Error,
    layoutComponent: SchoolLayout,
    getRoute: () => {
      return `/school-app/dashboard`;
    },
    roles: [UserRole.SchoolGroupAdmin],
  },
  [SchoolRouteKeys.SchoolGroupSchoolList]: {
    path: '/school-app/schools',
    component: SchoolGroupSchoolList,
    errorComponent: Error,
    layoutComponent: SchoolLayout,
    getRoute: () => {
      return `/school-app/schools`;
    },
    roles: [UserRole.SchoolGroupAdmin, UserRole.SchoolAdmin],
  },
  [SchoolRouteKeys.SchoolGroupClassroomList]: {
    path: '/school-app/classrooms',
    component: SchoolGroupClassroomList,
    errorComponent: Error,
    layoutComponent: SchoolLayout,
    getRoute: () => {
      return `/school-app/classrooms`;
    },
    roles: [UserRole.SchoolGroupAdmin, UserRole.SchoolAdmin, UserRole.Instructor],
  },
  [SchoolRouteKeys.SchoolGroupUserList]: {
    path: '/school-app/users',
    component: SchoolGroupUserList,
    errorComponent: Error,
    layoutComponent: SchoolLayout,
    getRoute: () => {
      return `/school-app/users`;
    },
    roles: [UserRole.SchoolGroupAdmin, UserRole.SchoolAdmin, UserRole.Instructor],
  },

  // Switchable
  [SchoolRouteKeys.SchoolClassroomDetail]: {
    path: '/school-app/schools/:schoolId/classrooms/:classroomId',
    component: (props) => <SchoolDashboardSwitcher {...props} routeKey={SchoolRouteKeys.SchoolClassroomDetail} />,
    errorComponent: Error,
    layoutComponent: SchoolLayout,
    getRoute: ({ schoolId, classroomId }: { schoolId: string; classroomId: string }) => {
      return `/school-app/schools/${schoolId}/classrooms/${classroomId}`;
    },
  },
  [SchoolRouteKeys.SchoolClassroomOverview]: {
    path: '/school-app/schools/:schoolId/classrooms/:classroomId/overview',
    component: (props) => <SchoolDashboardSwitcher {...props} routeKey={SchoolRouteKeys.SchoolClassroomOverview} />,
    errorComponent: Error,
    layoutComponent: SchoolLayout,
    getRoute: ({ schoolId, classroomId }: { schoolId: string; classroomId: string }) => {
      return `/school-app/schools/${schoolId}/classrooms/${classroomId}/overview`;
    },
  },
  [SchoolRouteKeys.SchoolClassroomInsights]: {
    path: '/school-app/schools/:schoolId/classrooms/:classroomId/insights',
    component: SchoolClassroomTaxonomyInsights,
    errorComponent: Error,
    layoutComponent: SchoolLayout,
    getRoute: ({ schoolId, classroomId }: { schoolId: string; classroomId: string }) =>
      `/school-app/schools/${schoolId}/classrooms/${classroomId}/insights`,
  },
  [SchoolRouteKeys.SchoolStudentList]: {
    path: '/school-app/schools/:schoolId/classrooms/:classroomId/students',
    component: (props) => <SchoolDashboardSwitcher {...props} routeKey={SchoolRouteKeys.SchoolStudentList} />,
    errorComponent: Error,
    layoutComponent: SchoolLayout,
    getRoute: ({ schoolId, classroomId }: { schoolId: string; classroomId: string }) => {
      return `/school-app/schools/${schoolId}/classrooms/${classroomId}/students`;
    },
  },
  [SchoolRouteKeys.SchoolStudentDetail]: {
    path: '/school-app/schools/:schoolId/classrooms/:classroomId/students/:studentId',
    component: (props) => <SchoolDashboardSwitcher {...props} routeKey={SchoolRouteKeys.SchoolStudentDetail} />,
    errorComponent: Error,
    layoutComponent: SchoolLayout,
    getRoute: ({ schoolId, classroomId, studentId }: { schoolId: string; classroomId: string; studentId: string }) => {
      return `/school-app/schools/${schoolId}/classrooms/${classroomId}/students/${studentId}`;
    },
  },
  [SchoolRouteKeys.SchoolLessonList]: {
    path: '/school-app/schools/:schoolId/classrooms/:classroomId/lessons',
    component: (props) => <SchoolDashboardSwitcher {...props} routeKey={SchoolRouteKeys.SchoolLessonList} />,
    errorComponent: Error,
    layoutComponent: SchoolLayout,
    getRoute: ({ classroomId, schoolId }: { classroomId: string; schoolId: string }) => {
      return `/school-app/schools/${schoolId}/classrooms/${classroomId}/lessons`;
    },
  },
  [SchoolRouteKeys.SchoolLessonDetail]: {
    path: '/school-app/schools/:schoolId/classrooms/:classroomId/lessons/:lessonId',
    component: (props) => <SchoolDashboardSwitcher {...props} routeKey={SchoolRouteKeys.SchoolLessonDetail} />,
    errorComponent: Error,
    layoutComponent: SchoolLayout,
    getRoute: ({ schoolId, classroomId, lessonId }: { schoolId: string; classroomId: string; lessonId: string }) => {
      return `/school-app/schools/${schoolId}/classrooms/${classroomId}/lessons/${lessonId}`;
    },
  },
  [SchoolRouteKeys.SchoolLiveSessionList]: {
    path: '/school-app/schools/:schoolId/classrooms/:classroomId/competition',
    component: SchoolLiveSessionList,
    errorComponent: Error,
    layoutComponent: SchoolLayout,
    getRoute: ({ schoolId, classroomId }: { schoolId: string; classroomId: string }) => {
      return `/school-app/schools/${schoolId}/classrooms/${classroomId}/competition`;
    },
  },
  [SchoolRouteKeys.SchoolLiveSessionHost]: {
    path: '/school-app/schools/:schoolId/classrooms/:classroomId/competition/:sessionId',
    component: SchoolLiveSessionHost,
    errorComponent: Error,
    layoutComponent: SchoolLayout,
    getRoute: ({ schoolId, classroomId, sessionId }: { schoolId: string; classroomId: string; sessionId: string }) => {
      return `/school-app/schools/${schoolId}/classrooms/${classroomId}/competition/${sessionId}`;
    },
  },
} satisfies Record<SchoolRouteKeys, RouteData>;

export { SchoolRouteKeys, schoolRoutes };
